import React from 'react';
import useAutoFocusFirstField from 'hooks/useAutoFocusFirstField';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { Button, TextField, IconButton } from '@material-ui/core';
import { Add, Delete, Refresh } from '@material-ui/icons';

import ConfirmDialog from 'components/ConfirmDialog';
import SelectField from 'components/SelectField';

import { mapToSelectOptions } from 'utils/select';
import { getApiErrorMessage } from 'utils/errors';

import { getVenuesWithStockModuleActive } from 'services/venuesServices';
import { getCommerceProductsAvailable, getStockWarehouses, getVenueWarehouses } from 'services/stockWarehouseServices';

import Venue from 'types/models/Venue';
import { LocationProduct, StockProductType, StockWarehouse } from 'types/models/StockReceipt';

import {
  StockMovement,
  StockMovementItem,
  StockMovementSourceType,
  StockMovementDestinationType,
} from 'types/models/StockMovement';
import { storeStockTransfer } from 'services/stockMovementsServices';
import { StockMovementTransferFormSchema } from './schemas/StockModuleForm';

import { getWarehouseProducts } from 'services/stockWarehouseServices';
import Commerce from 'types/models/Commerce';
import AppContext from 'contexts/AppContext';
import useSetupInitialState from 'hooks/useSetupInitialState';
import UserRoleType from 'types/enums/UserRoleType';
import { getCommercesByVenue } from 'services/commerceServices';
import SaveError from 'components/common/SaveError';

interface Props {
  movement?: StockMovement | null;
  isReadOnly?: boolean;
}

const EMPTY_PRODUCT: StockMovementItem = {
  id: '',
  globalProductId: 0,
  quantity: 0,

  createdAt: new Date().toISOString().slice(0, 10),
  stockMovementId: '',
  unitCost: 0,
};

export default function StockTransferForm({ movement, isReadOnly }: Props): JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();

  const { authData } = React.useContext(AppContext);
  const entity = useSetupInitialState();
  const isVenueEditor = authData?.user?.role === UserRoleType.VenueEditor;
  const isWarehouseEditor = authData?.user?.role === UserRoleType.WarehouseEditor;
  const [openDialog, setOpenDialog] = React.useState(false);

  const [locationProducts, setLocationProducts] = React.useState<LocationProduct[]>([]);
  const [venues, setVenues] = React.useState<Venue[]>([]);
  const [warehouses, setWarehouses] = React.useState<StockWarehouse[]>([]);

  const [commerces, setCommerces] = React.useState<Commerce[]>([]);
  const [products, setProducts] = React.useState<StockMovementItem[]>(
    movement?.items?.length ? movement.items : [{ ...EMPTY_PRODUCT }],
  );

  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const formRef = useAutoFocusFirstField(!isLoading && !loadError);

  React.useEffect(() => {
    let mounted = true;

    async function loadInitialData() {
      if (isVenueEditor) {
        if (mounted) setIsLoading(false);
        return;
      }

      if (isWarehouseEditor) {
        try {
          const myWarehouses = await getStockWarehouses(1, undefined, authData?.user?.id);
          const myWarehouse = myWarehouses?.data?.[0];
          if (myWarehouse) {
            formik.setFieldValue('venueId', myWarehouse.venueId);
            if (formik.values.sourceType === StockMovementSourceType.WAREHOUSE && !formik.values.sourceId) {
              formik.setFieldValue('sourceId', myWarehouse.id);
            }
          }
        } catch (error) {
          console.error(error);
          setLoadError(t('stock.receipts.error.no-necessary-data'));
        } finally {
          if (mounted) setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);

      try {
        const venuesResponse = await getVenuesWithStockModuleActive();

        if (!mounted) return;

        if (venuesResponse) setVenues(venuesResponse.data ?? []);
      } catch (error) {
        console.error(error);

        if (!mounted) return;

        setLoadError(t('stock.receipts.error.no-necessary-data'));
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    loadInitialData();

    return () => {
      mounted = false;
    };
  }, [isVenueEditor, isWarehouseEditor]);

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      venueId: movement?.venueId ?? entity?.id ?? '',

      sourceType: movement?.sourceType ?? StockMovementSourceType.WAREHOUSE,
      sourceId: movement?.sourceId ?? '',
      destinationType: movement?.destinationType ?? StockMovementDestinationType.WAREHOUSE,
      destinationId: movement?.destinationId ?? '',
    },

    validationSchema: () => StockMovementTransferFormSchema(t),
    onSubmit: () => setOpenDialog(true),
  });

  React.useEffect(() => {
    if (!formik.values.venueId) {
      setWarehouses([]);
      setCommerces([]);
      setLocationProducts([]);
      return;
    }

    async function loadVenueData() {
      try {
        const [warehousesData, commercesData] = await Promise.all([
          getVenueWarehouses(
            formik.values.venueId,
            isWarehouseEditor ? { managerUserId: authData?.user?.id } : undefined,
          ),
          getCommercesByVenue({ venueId: formik.values.venueId }),
        ]);

        setWarehouses(warehousesData);
        setCommerces(commercesData);

        if (!movement) {
          setLocationProducts([]);
          setProducts([{ ...EMPTY_PRODUCT }]);
        }
      } catch (error) {
        console.error(error);
      }
    }

    loadVenueData();
  }, [formik.values.venueId]);

  React.useEffect(() => {
    if (!formik.values.sourceType || !formik.values.sourceId) {
      setLocationProducts([]);
      if (!movement) setProducts([{ ...EMPTY_PRODUCT }]);
      return;
    }

    async function loadProducts() {
      try {
        if (formik.values.sourceType === StockMovementSourceType.WAREHOUSE) {
          const products = await getWarehouseProducts(formik.values.sourceId);
          setLocationProducts(products);
        } else {
          const products = await getCommerceProductsAvailable(formik.values.sourceId);
          setLocationProducts(products);
        }

        if (!movement) setProducts([{ ...EMPTY_PRODUCT }]);
      } catch (error) {
        console.error(error);
        setLocationProducts([]);
      }
    }

    loadProducts();
  }, [formik.values.sourceType, formik.values.sourceId]);

  function updateProduct(index: number, key: keyof StockMovementItem, value: any) {
    const copy = [...products];

    copy[index] = {
      ...copy[index],
      [key]: value,
    };

    setProducts(copy);
  }

  function removeProduct(index: number) {
    if (products.length === 1) {
      return;
    }

    setProducts((prev) => prev.filter((_, i) => i !== index));
  }

  function clearProduct(index: number) {
    const copy = [...products];

    copy[index] = {
      ...EMPTY_PRODUCT,
    };

    setProducts(copy);
  }

  function selectLocationProduct(index: number, globalProductId: number) {
    const product = locationProducts.find((p) => p.globalProductId === globalProductId);

    if (!product) {
      return;
    }

    updateProduct(index, 'globalProductId', product.globalProductId);

    const copy = [...products];

    copy[index] = {
      ...copy[index],
      id: product.commerceProductId ?? '',
      globalProductId: product.globalProductId,
      quantity: copy[index].quantity || 1,
      unitCost: 0,
    };

    setProducts(copy);
  }

  function getSelectedProduct(globalProductId: number | null | undefined) {
    return locationProducts.find((p) => p.globalProductId === globalProductId) ?? null;
  }

  function addProduct() {
    setProducts((prev) => [...prev, { ...EMPTY_PRODUCT }]);
  }

  async function onConfirm() {
    setSaveError(null);

    try {
      await storeStockTransfer({
        ...formik.values,
        createdByUserId: authData?.user.id,
        products,
      });

      history.go(-1);
    } catch (error) {
      console.error(error);

      setSaveError(getApiErrorMessage(error) ?? t('errors.generic'));

      formik.setSubmitting(false);
    }
  }

  if (isLoading) {
    return <div>{t('loading')}</div>;
  }

  if (loadError) {
    return <div>{loadError}</div>;
  }

  return (
    <>
      <ConfirmDialog
        open={openDialog}
        setOpen={setOpenDialog}
        onConfirm={onConfirm}
        onCancel={() => formik.setSubmitting(false)}
      />

      <h1>{t('stock.forms.transfer-title')}</h1>

      <form onSubmit={formik.handleSubmit} ref={formRef}>
        {!isVenueEditor && (
          <SelectField
            name="venueId"
            label={t('stock.forms.venue')}
            placeholder={t('stock.forms.venue-placeholder')}
            value={formik.values.venueId}
            options={mapToSelectOptions(venues, {
              label: 'name',
            })}
            isMulti={false}
            setFieldValue={formik.setFieldValue}
            onBlur={formik.handleBlur}
            touched={formik.touched.venueId}
            error={formik.errors.venueId}
            isClearable={false}
            backspaceRemovesValue={false}
            isDisabled={formik.isSubmitting}
            isSearchable
          />
        )}

        <SelectField
          name="sourceType"
          isSearchable
          label={t('stock.forms.source-type')}
          placeholder={t('stock.forms.select')}
          value={formik.values.sourceType}
          options={[
            {
              label: t('stock.forms.warehouse'),
              value: StockMovementSourceType.WAREHOUSE,
            },
            {
              label: t('stock.forms.commerce'),
              value: StockMovementSourceType.COMMERCE,
            },
          ]}
          isMulti={false}
          setFieldValue={(name, value) => {
            formik.setFieldValue(name, value);
            if (!movement) formik.setFieldValue('sourceId', '');
          }}
          onBlur={formik.handleBlur}
          touched={formik.touched.sourceType}
          error={formik.errors.sourceType}
          isClearable={false}
          backspaceRemovesValue={false}
          isDisabled={formik.isSubmitting}
        />

        <SelectField
          name="sourceId"
          isSearchable
          label={t('stock.forms.origin')}
          placeholder={t('stock.forms.select')}
          value={formik.values.sourceId}
          options={mapToSelectOptions(
            formik.values.sourceType === StockMovementSourceType.WAREHOUSE ? warehouses : (commerces as any),
            {
              label: 'name',
            },
          )}
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.sourceId}
          error={formik.errors.sourceId}
          isClearable={false}
          backspaceRemovesValue={false}
          isDisabled={formik.isSubmitting}
        />

        <SelectField
          name="destinationType"
          isSearchable
          label={t('stock.forms.destination-type')}
          placeholder={t('stock.forms.select')}
          value={formik.values.destinationType}
          options={[
            {
              label: t('stock.forms.warehouse'),
              value: StockMovementDestinationType.WAREHOUSE,
            },
            {
              label: t('stock.forms.commerce'),
              value: StockMovementDestinationType.COMMERCE,
            },
          ]}
          isMulti={false}
          setFieldValue={(name, value) => {
            formik.setFieldValue(name, value);
            if (!movement) formik.setFieldValue('destinationId', '');
          }}
          onBlur={formik.handleBlur}
          touched={formik.touched.destinationType}
          error={formik.errors.destinationType}
          isClearable={false}
          backspaceRemovesValue={false}
          isDisabled={formik.isSubmitting}
        />

        <SelectField
          name="destinationId"
          isSearchable
          label={t('stock.forms.destination')}
          placeholder={t('stock.forms.select')}
          value={formik.values.destinationId}
          options={mapToSelectOptions(
            formik.values.destinationType === StockMovementDestinationType.WAREHOUSE ? warehouses : (commerces as any),
            {
              label: 'name',
            },
          )}
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.destinationId}
          error={formik.errors.destinationId}
          isClearable={false}
          backspaceRemovesValue={false}
          isDisabled={formik.isSubmitting}
        />

        <hr />
        <h3>{t('stock.forms.products')}</h3>

        {products.map((product, index) => {
          const selectedProduct = getSelectedProduct(product.globalProductId);

          return (
            <div
              key={index}
              style={{
                border: '1px solid #DDD',
                padding: 15,
                marginBottom: 15,
              }}
            >
              <SelectField
                name={`product-${index}`}
                label={t('stock.forms.product')}
                placeholder={t('stock.forms.product-placeholder')}
                value={product.globalProductId}
                options={locationProducts.map((p) => ({
                  value: p.globalProductId,
                  label: `${p.name} (${p.sku ?? ''})`,
                }))}
                isMulti={false}
                setFieldValue={(_, value) => selectLocationProduct(index, Number(value))}
                touched={false}
                error={false}
                isDisabled={formik.isSubmitting}
                isClearable
                isSearchable
                backspaceRemovesValue
                onBlur={() => undefined}
              />

              <TextField
                margin="dense"
                fullWidth
                label={t('stock.forms.sku')}
                value={selectedProduct?.sku ?? ''}
                disabled
                InputProps={{
                  readOnly: true,
                }}
              />

              <TextField
                margin="dense"
                fullWidth
                label={t('stock.forms.name')}
                value={selectedProduct?.name ?? ''}
                disabled
                InputProps={{
                  readOnly: true,
                }}
              />

              <TextField
                margin="dense"
                fullWidth
                label={t('stock.forms.type')}
                value={
                  selectedProduct
                    ? selectedProduct.type === StockProductType.FINISHED
                      ? t('stock.forms.finished-product')
                      : t('stock.forms.raw-material')
                    : ''
                }
                disabled
                InputProps={{
                  readOnly: true,
                }}
              />

              <TextField
                margin="dense"
                fullWidth
                label={t('stock.forms.available-stock')}
                value={selectedProduct?.stock ?? ''}
                disabled
                InputProps={{
                  readOnly: true,
                }}
              />

              <TextField
                margin="dense"
                fullWidth
                type="number"
                label={t('stock.forms.quantity-to-transfer')}
                value={product.quantity}
                onChange={(e) => updateProduct(index, 'quantity', Number(e.target.value))}
                inputProps={{
                  min: 0.01,
                  max: selectedProduct?.stock ?? undefined,
                  step: 0.01,
                  readOnly: !!movement,
                }}
              />

              {movement ? null : (
                <div
                  style={{
                    marginTop: 10,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}
                >
                  <IconButton color="primary" onClick={() => clearProduct(index)}>
                    <Refresh />
                  </IconButton>

                  <IconButton color="primary" onClick={addProduct}>
                    <Add />
                  </IconButton>

                  {products.length > 1 && (
                    <IconButton color="secondary" onClick={() => removeProduct(index)}>
                      <Delete />
                    </IconButton>
                  )}
                </div>
              )}
            </div>
          );
        })}

        <SaveError error={saveError} />

        {!isReadOnly && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 25,
            }}
          >
            <Button variant="contained" color="primary" type="submit" disabled={formik.isSubmitting}>
              {t('stock.forms.create-transfer')}
            </Button>
          </div>
        )}
      </form>
    </>
  );
}
